import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from "@mui/material";
import { Schedule, Layers, Payments } from "@mui/icons-material";
import { useDashboard } from "@/hooks/useDashboard";
import { formatHoursMinutes } from "@/utils/format";
import {
  PAGE_MAX_WIDTH,
  WEEKLY_HOURS_TARGET,
  GAUGE_ARC_LENGTH,
  LABEL_CAPS_SX,
} from "@/constants";
import { PageLoading } from "@/components/common/PageLoading";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyTableRow } from "@/components/common/EmptyTableRow";
import { TableHeaderCell } from "@/components/common/TableHeaderCell";
import { PulseDot } from "@/components/common/PulseDot";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function DashboardPage() {
  const { t } = useTranslation();
  const { stats, dailyHours, recent, loading, maxDailyHours } = useDashboard();

  if (loading) return <PageLoading />;

  return (
    <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: "auto" }}>
      <PageHeader title={t("dashboard.title")} />
      <StatsRow stats={stats} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "2fr 1fr" },
          gap: 3,
          mb: 4,
        }}
      >
        <DailyHoursChart dailyHours={dailyHours} maxHours={maxDailyHours} />
        <CapacityGauge stats={stats} />
      </Box>
      <RecentActivitiesTable entries={recent} />
    </Box>
  );
}

function StatsRow({ stats }: { stats: any }) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: 3,
        mb: 4,
      }}
    >
      <Card variant="outlined">
        <CardContent>
          <Schedule sx={{ color: "primary.main", fontSize: 32, mb: 1 }} />
          <Typography
            variant="caption"
            sx={[
              {
                color: "text.secondary",
              },
              ...(Array.isArray(LABEL_CAPS_SX)
                ? LABEL_CAPS_SX
                : [LABEL_CAPS_SX]),
            ]}
          >
            {t("dashboard.totalHours")}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mt: 0.5,
            }}
          >
            {stats?.totalHours?.toFixed(1) || "0"}h
          </Typography>
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(
                ((stats?.totalHours || 0) / WEEKLY_HOURS_TARGET) * 100,
                100,
              )}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Layers sx={{ color: "secondary.main", fontSize: 32, mb: 1 }} />
          <Typography
            variant="caption"
            sx={[
              {
                color: "text.secondary",
              },
              ...(Array.isArray(LABEL_CAPS_SX)
                ? LABEL_CAPS_SX
                : [LABEL_CAPS_SX]),
            ]}
          >
            {t("dashboard.activeProjects")}
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mt: 0.5,
            }}
          >
            {stats?.activeProjects || 0}
          </Typography>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Payments sx={{ color: "warning.main", fontSize: 32, mb: 1 }} />
          <Typography
            variant="caption"
            sx={[
              {
                color: "text.secondary",
              },
              ...(Array.isArray(LABEL_CAPS_SX)
                ? LABEL_CAPS_SX
                : [LABEL_CAPS_SX]),
            ]}
          >
            {t("dashboard.billableRatio")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
              }}
            >
              {stats?.billableRatio || 0}%
            </Typography>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                height: 16,
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${stats?.billableRatio || 0}%`,
                  bgcolor: "warning.main",
                }}
              />
              <Box sx={{ flex: 1, bgcolor: "action.hover" }} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function DailyHoursChart({
  dailyHours,
  maxHours,
}: {
  dailyHours: any[];
  maxHours: number;
}) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
          }}
        >
          {t("dashboard.timePerDay")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            height: 200,
            gap: 1,
            px: 2,
          }}
        >
          {dailyHours.length > 0 ? (
            dailyHours.map((d) => (
              <Box
                key={d.date}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    bgcolor: "primary.main",
                    borderRadius: "4px 4px 0 0",
                    height: `${(d.hours / maxHours) * 180}px`,
                    minHeight: 4,
                    opacity: 0.8,
                    "&:hover": { opacity: 1 },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "text.secondary",
                  }}
                >
                  {WEEKDAYS[new Date(d.date).getDay()] || d.date.slice(5)}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography
              sx={{
                color: "text.secondary",
                m: "auto",
              }}
            >
              {t("common.noData")}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

function CapacityGauge({ stats }: { stats: any }) {
  const { t } = useTranslation();
  const ratio = stats?.billableRatio || 0;
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
          }}
        >
          {t("dashboard.capacityUtil")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 160,
              height: 80,
              overflow: "hidden",
            }}
          >
            <svg viewBox="0 0 100 50" style={{ width: "100%", height: "100%" }}>
              <path
                d="M 5 50 A 45 45 0 0 1 95 50"
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="8"
              />
              <path
                d="M 5 50 A 45 45 0 0 1 95 50"
                fill="none"
                stroke="#004ac6"
                strokeWidth="8"
                strokeDasharray={`${(ratio / 100) * GAUGE_ARC_LENGTH} ${GAUGE_ARC_LENGTH}`}
                strokeLinecap="round"
              />
            </svg>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                position: "absolute",
                bottom: 0,
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {ratio}%
            </Typography>
          </Box>
          <Box sx={{ width: "100%", mt: 4 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("dashboard.billable")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                }}
              >
                {stats?.billableHours?.toFixed(1) || 0}h
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                {t("dashboard.total")}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                }}
              >
                {stats?.totalHours?.toFixed(1) || 0}h
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function RecentActivitiesTable({ entries }: { entries: any[] }) {
  const { t } = useTranslation();
  return (
    <Card variant="outlined">
      <Box sx={{ px: 3, py: 2, borderBottom: 1, borderColor: "divider" }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("dashboard.recentActivities")}
        </Typography>
      </Box>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableHeaderCell>{t("tracker.description")}</TableHeaderCell>
              <TableHeaderCell>{t("tracker.project")}</TableHeaderCell>
              <TableHeaderCell>{t("tracker.duration")}</TableHeaderCell>
              <TableHeaderCell>{t("projects.status")}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry: any) => {
              const isActive = !entry.through;
              const durationMs = isActive
                ? Date.now() - new Date(entry.from).getTime()
                : new Date(entry.through).getTime() -
                  new Date(entry.from).getTime();
              return (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isActive && <PulseDot />}
                      <Typography
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {entry.description || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.project?.name || "-"}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                      }}
                    >
                      {formatHoursMinutes(durationMs)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        isActive
                          ? t("tracker.active")
                          : entry.billable
                            ? t("tracker.billable")
                            : t("tracker.nonBillable")
                      }
                      size="small"
                      color={isActive ? "success" : "default"}
                      variant="outlined"
                      sx={{ fontSize: 10, fontWeight: 700 }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {entries.length === 0 && <EmptyTableRow colSpan={4} />}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
